/* eslint-disable @typescript-eslint/no-explicit-any */

import ApiRequest, { type ApiResponse } from "shared/api/api-request-builder";
import { type HttpMethod } from "shared/api/http";
import {
  type PaginationRequest,
  type PaginatedResults,
} from "shared/api/types/pagination";
import { type FiltersRequest } from "shared/filter-where-clause";

export interface BaseApiContract<
  ParamsType = any,
  BodyType = any,
  ResponseType = any,
> {
  method: HttpMethod;
  apiRoute: string;
  paramsType?: ParamsType;
  bodyType?: BodyType;
  responseType: ResponseType;
}
interface Params {
  [key: string]: number | string | (number | string)[];
}
export type ExtractContractInputs<T> = T extends {
  paramsType?: infer P;
  bodyType?: infer B;
}
  ? { params?: P; body?: B }
  : never;

// The apiRequest function adjusted to use the contract for typing
/*
 * @deprecated use apiRequestContractHandlerPropsInput instead
 */
export const apiRequestContractHandler = async <T extends BaseApiContract>(
  contract: T,
  inputs?: ExtractContractInputs<T>,
  filters?: FiltersRequest,
  pagination?: PaginationRequest,
  customHeaders?: HeadersInit,
  customParser?: (Response: Response) => ApiResponse["body"],
  returnRawResponse = false,
): Promise<T["responseType"]> => {
  const params = inputs?.params ? inputs.params : {};

  //Do function to replace the dynamic values in the apiRoute
  const url = buildUrl(contract.apiRoute, params as Params);

  //Actually do the API request and return the response
  const response = await ApiRequest(
    url,
    contract.method,
    inputs?.body,
    customHeaders,
    customParser,
    filters,
    pagination,
    returnRawResponse,
  );
  return response?.body as T["responseType"];
};
const MAX_PARALLEL_REQUESTS = 5;
const TARGET_PAYLOAD_BYTES = 500_000; // ~500KB target per request
const MIN_PAGE_SIZE = 25;
const MAX_PAGE_SIZE = 500;

const estimatePageSize = (
  results: unknown[],
  currentPageSize: number,
): number => {
  if (results.length === 0) {
    return currentPageSize;
  }
  const payloadBytes = JSON.stringify(results).length;
  const bytesPerItem = payloadBytes / results.length;
  if (bytesPerItem === 0) {
    return currentPageSize;
  }
  const estimated = Math.floor(TARGET_PAYLOAD_BYTES / bytesPerItem);
  return Math.max(MIN_PAGE_SIZE, Math.min(MAX_PAGE_SIZE, estimated));
};

const fetchAllPaginatedResults = async <T extends BaseApiContract>(props: {
  contract: T;
  inputs?: ExtractContractInputs<T>;
  filters?: FiltersRequest;
  pagination?: PaginationRequest;
  customHeaders?: HeadersInit;
  customParser?: (Response: Response) => ApiResponse["body"];
  returnRawResponse?: boolean;
}): Promise<T["responseType"]> => {
  const initialPageSize = props.pagination?.pageSize ?? 100;

  // First request to get totalResults and estimate optimal page size
  const firstPage = await apiRequestContractHandler(
    props.contract,
    props.inputs,
    props.filters,
    { ...props.pagination, page: 1, pageSize: initialPageSize },
    props.customHeaders,
    props.customParser,
    props.returnRawResponse,
  );

  if (!("results" in firstPage)) {
    return firstPage;
  }

  const { results: firstResults, totalResults } = firstPage;
  if (totalResults <= firstResults.length) {
    return firstPage;
  }

  const optimalPageSize = estimatePageSize(firstResults, initialPageSize);
  const totalPages = Math.ceil(totalResults / optimalPageSize);

  // Reuse first page results when page size hasn't changed
  const canReuseFirstPage = optimalPageSize === initialPageSize;
  const allResults: T["responseType"][][] = canReuseFirstPage
    ? [firstResults]
    : [];
  const startPage = canReuseFirstPage ? 2 : 1;

  const pagesToFetch = Array.from(
    { length: totalPages - startPage + 1 },
    (_, i) => i + startPage,
  );

  for (let i = 0; i < pagesToFetch.length; i += MAX_PARALLEL_REQUESTS) {
    const batch = pagesToFetch.slice(i, i + MAX_PARALLEL_REQUESTS);
    const batchResults = await Promise.all(
      batch.map((page) =>
        apiRequestContractHandler(
          props.contract,
          props.inputs,
          props.filters,
          { ...props.pagination, page, pageSize: optimalPageSize },
          props.customHeaders,
          props.customParser,
          props.returnRawResponse,
        ),
      ),
    );
    for (const res of batchResults) {
      if (res && typeof res === "object" && "results" in res) {
        allResults.push((res as PaginatedResults).results);
      }
    }
  }

  return {
    ...firstPage,
    results: allResults.flat(),
    page: 1,
    totalResults,
    pageSize: totalResults,
  };
};

export const apiRequestContractHandlerPropsInput = async <
  T extends BaseApiContract,
>(props: {
  contract: T;
  inputs?: ExtractContractInputs<T>;
  filters?: FiltersRequest;
  pagination?: PaginationRequest;
  customHeaders?: HeadersInit;
  customParser?: (Response: Response) => ApiResponse["body"];
  returnRawResponse?: boolean;
  flattenPagination?: boolean;
}): Promise<T["responseType"]> => {
  if (props.flattenPagination) {
    return await fetchAllPaginatedResults(props);
  }
  return apiRequestContractHandler(
    props.contract,
    props.inputs,
    props.filters,
    props.pagination,
    props.customHeaders,
    props.customParser,
    props.returnRawResponse,
  );
};

export const replaceTemplate = (
  template: string,
  params: Params,
): { result: string; usedKeys: Set<string> } => {
  const usedKeys = new Set<string>();

  // [[...key]]
  let result = template.replace(/\[\[\.\.\.([^\]]+)\]\]/g, (match, p1) => {
    usedKeys.add(p1);
    const values = params[p1];
    if (values && Array.isArray(values) && values.length > 0) {
      return values.map((value) => String(value)).join("/");
    } else if (values) {
      return String(values);
    } else {
      return "";
    }
  });

  // [...key]
  result = result.replace(/\[\.\.\.([^\]]+)\]/g, (match, p1) => {
    usedKeys.add(p1);
    const values = params[p1];
    if (values && Array.isArray(values) && values.length > 0) {
      return values.map((value) => String(value)).join("/");
    } else if (values) {
      return String(values);
    } else {
      return match;
    }
  });

  // [[key]]
  result = result.replace(/\[\[([^\]]+)\]\]/g, (match, p1) => {
    usedKeys.add(p1);
    return params[p1] !== undefined ? String(params[p1]) : "";
  });

  // [key]
  result = result.replace(/\[([^\]]+)\]/g, (match, p1) => {
    usedKeys.add(p1);
    return params[p1] !== undefined ? String(params[p1]) : match;
  });

  return { result, usedKeys };
};

const buildQueryParams = (params: Params, usedKeys: Set<string>): string => {
  const queryParams = Object.keys(params)
    .filter((key) => !usedKeys.has(key))
    .map((key) => {
      const value = params[key];
      if (Array.isArray(value)) {
        return value
          .map(
            (v) =>
              `${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`,
          )
          .join("&");
      } else {
        return `${encodeURIComponent(key)}=${encodeURIComponent(
          String(value),
        )}`;
      }
    })
    .join("&");

  return queryParams ? `?${queryParams}` : "";
};

export const buildUrl = (template: string, params: Params): string => {
  const baseUrl = process.env.REACT_APP_PILLAR_API_URL || "";
  const { result, usedKeys } = replaceTemplate(template, params);
  const queryParams = buildQueryParams(params, usedKeys);

  return `${baseUrl}${result}${queryParams}`;
};
